// Данные экрана из хранилища: строки, задачи по строкам, исполнители, чужое внимание.
import { useProjectOrder } from './order';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@core/auth/store';
import { data, useData, useTable } from '@core/store';
import type { Assignee, Task } from './planner';
import { ATTENTION_HOLD_MS, attentionOf, dropExpired, fadeStep, mergeAttention, type Attention, type Fading } from './attention';

export type { Attention } from './attention';
export type { Assignee, Task } from './planner';
export const EVENT_DEFAULT_COLOR = '#4f46e5'; // у перенесённых событий цвета нет
export const RELEASES_LANE = '00000000-0000-0000-0000-00000000a001'; // лента Releases из 0012: новый проект по умолчанию в неё
/** header — проект с подпроектами: строка-заголовок в цвете проекта, задачи заводятся только в подпроектах. */
export type RowDef = { key: string; project_id: string; subproject_id: string | null; label: string; isSub: boolean; header: boolean; color: string };

export const rowKeyOf = (project_id: string | null, subproject_id: string | null) => `${project_id}:${subproject_id ?? ''}`;
export const MAX_ASSIGNEES = 3;

/** «Shura: Bali» — подпись отпуска в чипах и тултипах; без заметки — просто «vacation». */
export const vacationLabel = (v: { note: string | null }, person: { name: string } | undefined): string => `${person?.name ?? 'Vacation'}: ${v.note || 'vacation'}`;

// Подтверждённое состояние прямо из хранилища — для планирования записи. Замыкания React
// отстают на рендер, а expect_updated_at должен быть свежим.
export const taskNow = (id: string): Task | undefined => data.store.getState().tables.task.get(id);
export const rowTasksNow = (key: string): Task[] =>
  [...data.store.getState().tables.task.values()].filter((t) => !t.in_backlog && rowKeyOf(t.project_id, t.subproject_id) === key);
export const rowTasksOf = (k: { project_id: string | null; subproject_id: string | null }): Task[] => rowTasksNow(rowKeyOf(k.project_id, k.subproject_id));
/** Подтверждённые строки по списку id; исчезнувшие пропускаются. */
export const tasksNow = (ids: Iterable<string>): Task[] => [...ids].map(taskNow).filter((t): t is Task => !!t);
/** Назначения из подтверждённого состояния — для планирования копий и переназначений. */
export const assigneesNow = (): Assignee[] => [...data.store.getState().tables.task_assignee.values()];
/** Свежая строка задачи, если она ещё есть; иначе та, что была в руках. */
export const freshOf = (task: Task): Task => taskNow(task.id) ?? task;

/** Задачи «нужен дизайн» на сетке, по дате начала; с range — только задетые видимым периодом (счётчик и панель Design). */
export function designNeeded(tasks: Iterable<Task>, range?: { from: string; to: string }): Task[] {
  return [...tasks]
    .filter((t) => t.design_status === 'needed' && !t.in_backlog && (!range || (t.start_date <= range.to && t.end_date >= range.from)))
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
}

export function useRows(): RowDef[] {
  const { rows: ps } = useProjectOrder();
  const subprojects = useTable('subproject');
  return useMemo(() => {
    const rows: RowDef[] = [];
    for (const p of ps) {
      const subs = [...subprojects.values()].filter((s) => s.project_id === p.id).sort((a, b) => a.position - b.position);
      rows.push({ key: rowKeyOf(p.id, null), project_id: p.id, subproject_id: null, label: p.name, isSub: false, header: subs.length > 0, color: p.color });
      for (const s of subs) rows.push({ key: rowKeyOf(p.id, s.id), project_id: p.id, subproject_id: s.id, label: s.name, isSub: true, header: false, color: p.color });
    }
    return rows;
  }, [ps, subprojects]);
}

/** allTasks — вся таблица задач (нужна карточке); byRow — по строкам сетки, backlog — отдельно. */
export function useRoadmapTasks(): { byRow: Map<string, Task[]>; backlog: Task[]; allTasks: Map<string, Task> } {
  const tasks = useTable('task');
  return useMemo(() => {
    const byRow = new Map<string, Task[]>();
    const backlog: Task[] = [];
    for (const t of tasks.values()) {
      if (t.in_backlog) { backlog.push(t); continue; }
      const k = rowKeyOf(t.project_id, t.subproject_id);
      const arr = byRow.get(k) ?? [];
      arr.push(t);
      byRow.set(k, arr);
    }
    backlog.sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
    return { byRow, backlog, allTasks: tasks };
  }, [tasks]);
}

/** Исполнители по задаче в порядке position: цвета для фона бара и имена для подсказки — одним проходом, в одном порядке. */
export function useAssignees(): { colors: Map<string, string[]>; names: Map<string, string[]> } {
  const assignees = useTable('task_assignee');
  const people = useTable('person');
  return useMemo(() => {
    const byTask = new Map<string, { color: string; name: string; position: number }[]>();
    for (const a of assignees.values()) {
      const person = people.get(a.person_id);
      if (!person) continue;
      byTask.set(a.task_id, [...(byTask.get(a.task_id) ?? []), { color: person.color, name: person.name, position: a.position }]);
    }
    const colors = new Map<string, string[]>();
    const names = new Map<string, string[]>();
    for (const [id, list] of byTask) {
      list.sort((a, b) => a.position - b.position);
      colors.set(id, list.map((x) => x.color));
      names.set(id, list.map((x) => x.name));
    }
    return { colors, names };
  }, [assignees, people]);
}

/**
 * Чужое внимание: задачи, которые у других выделены, в руках или открыты в карточке, — рамка цвета
 * человека, имя на первой из его набора. После того как человек снял выделение или закрыл карточку,
 * рамка держится ещё ATTENTION_HOLD_MS: чтобы было видно, где он только что работал.
 */
export function useAttention(): Map<string, Attention> {
  const presence = useData((s) => s.presence);
  const me = useAuth((s) => s.profile?.id);
  const [fading, setFading] = useState<Fading>(new Map());
  const live = useMemo(() => attentionOf(Object.values(presence), me), [presence, me]);
  const prev = useRef(live);
  useEffect(() => {
    const was = prev.current;
    prev.current = live;
    setFading((f) => fadeStep(was, live, f, Date.now() + ATTENTION_HOLD_MS));
  }, [live]);
  useEffect(() => {
    if (fading.size === 0) return;
    const soonest = Math.min(...[...fading.values()].map((a) => a.until));
    const t = setTimeout(() => setFading((f) => dropExpired(f, Date.now())), Math.max(0, soonest - Date.now()) + 20);
    return () => clearTimeout(t);
  }, [fading]);
  return useMemo(() => mergeAttention(live, fading), [live, fading]);
}
